import { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  fetchInstagramOverview, fetchInstagramPosts, fetchInstagramReels, fetchInstagramDemographics,
} from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  Instagram, Users, Eye, Heart, MessageSquare, Bookmark, Share2, Film, Image as ImageIcon,
  TrendingUp, TrendingDown, Target, MousePointerClick, UserPlus, Hash, ExternalLink, Globe2,
} from "lucide-react";

const RANGE_OPTIONS = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
  { value: 365, label: "12 meses" },
];

function getDateRange(days) {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - days);
  const iso = (d) => d.toISOString().split("T")[0];
  return { date_from: iso(from), date_to: iso(today) };
}

const fmt = (n) => (n === null || n === undefined ? "0" : Number(n).toLocaleString("es-AR"));
const fmtDec = (n, max = 1) =>
  n == null ? "0" : Number(n).toLocaleString("es-AR", { maximumFractionDigits: max });
const fmtPct = (n) => (n == null ? "0%" : `${Number(n).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function InstagramSection({ clientId }) {
  const [rangeDays, setRangeDays] = useState(30);
  const [innerTab, setInnerTab] = useState("comunidad");
  const range = useMemo(() => getDateRange(rangeDays), [rangeDays]);

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ["ig-overview", clientId, range.date_from, range.date_to],
    queryFn: () => fetchInstagramOverview(clientId, range.date_from, range.date_to),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: postsData, isLoading: loadingPosts } = useQuery({
    queryKey: ["ig-posts", clientId, range.date_from, range.date_to],
    queryFn: () => fetchInstagramPosts(clientId, range.date_from, range.date_to),
    enabled: !!overview?.configured && innerTab === "publicaciones",
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: reelsData, isLoading: loadingReels } = useQuery({
    queryKey: ["ig-reels", clientId, range.date_from, range.date_to],
    queryFn: () => fetchInstagramReels(clientId, range.date_from, range.date_to),
    enabled: !!overview?.configured && innerTab === "reels",
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: demographics } = useQuery({
    queryKey: ["ig-demographics", clientId, range.date_from, range.date_to],
    queryFn: () => fetchInstagramDemographics(clientId, range.date_from, range.date_to),
    enabled: !!overview?.configured && innerTab === "cuenta",
    staleTime: 30 * 60 * 1000,
  });

  // Build chart from primary channel followers + reach timelines (declared before any early return).
  const primaryChannel = overview?.channels?.[0];
  const chartData = useMemo(() => {
    const tl = primaryChannel?.timelines || {};
    const followers = tl.followers || [];
    const reach = tl.reach || [];
    const byDate = {};
    followers.forEach((p) => {
      const d = (p.dateTime || "").slice(0, 10);
      if (!d) return;
      byDate[d] = byDate[d] || { date: d };
      byDate[d].followers = Number(p.value || 0);
    });
    reach.forEach((p) => {
      const d = (p.dateTime || "").slice(0, 10);
      if (!d) return;
      byDate[d] = byDate[d] || { date: d };
      byDate[d].reach = Number(p.value || 0);
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [primaryChannel]);

  if (loadingOverview) {
    return (
      <div className="space-y-4" data-testid="ig-loading">
        <div className="h-24 rounded-md skeleton" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-md skeleton" />)}
        </div>
        <div className="h-72 rounded-md skeleton" />
      </div>
    );
  }

  if (!overview?.configured) {
    return (
      <div
        className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center"
        data-testid="ig-not-configured"
      >
        <Instagram className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <h3 className="font-display text-base font-semibold text-slate-900">Instagram no configurado</h3>
        <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
          {overview?.message || "Este cliente no tiene una cuenta de Instagram configurada en Metricool."}
        </p>
      </div>
    );
  }

  const totals = overview.totals || {};
  const channels = overview.channels || [];

  return (
    <div className="space-y-5" data-testid="ig-section">
      {/* Header + range selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-fuchsia-500 to-pink-500 border border-pink-300 flex items-center justify-center shadow-sm">
            <Instagram className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900 leading-tight">Instagram</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {channels.length === 1
                ? <>@{channels[0].handle} · {channels[0].label}</>
                : `${channels.length} cuentas conectadas`} · vía Metricool
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="ig-range-selector">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setRangeDays(opt.value)}
              data-testid={`ig-range-${opt.value}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                rangeDays === opt.value
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Inner sub-tabs */}
      <Tabs value={innerTab} onValueChange={setInnerTab} className="w-full">
        <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md inline-flex gap-1">
          <TabsTrigger value="comunidad" data-testid="ig-tab-comunidad"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
            <Users className="h-3.5 w-3.5" /> Comunidad
          </TabsTrigger>
          <TabsTrigger value="cuenta" data-testid="ig-tab-cuenta"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
            <Target className="h-3.5 w-3.5" /> Cuenta
          </TabsTrigger>
          <TabsTrigger value="publicaciones" data-testid="ig-tab-publicaciones"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
            <ImageIcon className="h-3.5 w-3.5" /> Publicaciones
          </TabsTrigger>
          <TabsTrigger value="reels" data-testid="ig-tab-reels"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
            <Film className="h-3.5 w-3.5" /> Reels
          </TabsTrigger>
        </TabsList>

        {/* COMUNIDAD */}
        <TabsContent value="comunidad" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="ig-community-kpis">
            <KpiCard icon={Users} label="Seguidores" value={fmt(totals.followers)} tone="pink" testid="ig-kpi-followers" />
            <KpiCard
              icon={totals.delta_followers >= 0 ? TrendingUp : TrendingDown}
              label="Variación en período"
              value={(totals.delta_followers >= 0 ? "+" : "") + fmt(totals.delta_followers)}
              tone={totals.delta_followers >= 0 ? "emerald" : "rose"}
              testid="ig-kpi-delta"
            />
            <KpiCard icon={UserPlus} label="Siguiendo" value={fmt(totals.following)} tone="slate" testid="ig-kpi-following" />
            <KpiCard
              icon={ImageIcon}
              label="Posts + Reels en período"
              value={fmt((totals.posts_in_period || 0) + (totals.reels_in_period || 0))}
              tone="amber"
              testid="ig-kpi-postscount"
            />
          </div>
          {(totals.posts_in_period || 0) + (totals.reels_in_period || 0) === 0 && (
            <p className="text-[11px] text-slate-500 italic" data-testid="ig-no-publications-hint">
              No hay publicaciones nuevas en este rango. Probá un rango mayor (90 días o 12 meses).
            </p>
          )}

          {chartData.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="ig-evolution-chart">
              <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Evolución · {primaryChannel?.label}</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="igGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={fmtDate} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "none", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: "#e2e8f0" }}
                    itemStyle={{ color: "#fff" }}
                  />
                  <Area type="monotone" dataKey="followers" name="Seguidores" stroke="#ec4899" fill="url(#igGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="reach" name="Alcance" stroke="#8b5cf6" fill="transparent" strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {channels.length > 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ig-channels-grid">
              {channels.map((ch) => (
                <div key={ch.blog_id} className="bg-white border border-slate-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    {ch.picture && <img src={ch.picture} alt="" className="w-8 h-8 rounded-full object-cover" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{ch.label}</div>
                      <a
                        href={`https://www.instagram.com/${ch.handle}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-pink-600 hover:underline flex items-center gap-1"
                      >
                        @{ch.handle} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-base font-semibold text-slate-900">{fmt(ch.followers)}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Subs</div></div>
                    <div><div className="text-base font-semibold text-slate-900">{fmt(ch.reach)}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Alcance</div></div>
                    <div><div className="text-base font-semibold text-slate-900">{fmt(ch.interactions)}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Inter.</div></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CUENTA */}
        <TabsContent value="cuenta" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="ig-account-kpis">
            <KpiCard icon={Eye} label="Alcance" value={fmt(totals.reach)} tone="pink" testid="ig-kpi-reach" />
            <KpiCard icon={Eye} label="Impresiones" value={fmt(totals.views)} tone="purple" testid="ig-kpi-views" />
            <KpiCard icon={Users} label="Visitas al perfil" value={fmt(totals.profile_views)} tone="sky" testid="ig-kpi-profile-views" />
            <KpiCard icon={Heart} label="Cuentas con interacción" value={fmt(totals.accounts_engaged)} tone="emerald" testid="ig-kpi-engaged" />
            <KpiCard icon={MousePointerClick} label="Clics de contacto" value={fmt(totals.contact_taps)} tone="amber" testid="ig-kpi-contact" />
            <KpiCard icon={Target} label="Total interacciones" value={fmt(totals.interactions)} tone="indigo" testid="ig-kpi-interactions" />
          </div>
          <IGDemographicsBlock data={demographics} />
        </TabsContent>

        {/* PUBLICACIONES */}
        <TabsContent value="publicaciones" className="mt-4 space-y-4" data-testid="ig-posts-tab">
          {loadingPosts ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-md skeleton" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="ig-posts-summary">
                <SmallStat label="Posts" value={fmt(postsData?.summary?.count)} />
                <SmallStat label="Alcance prom." value={fmtDec(postsData?.summary?.avg_reach, 0)} />
                <SmallStat label="Engagement" value={fmtPct(postsData?.summary?.avg_engagement)} />
                <SmallStat label="Likes" value={fmt(postsData?.summary?.likes)} />
                <SmallStat label="Comentarios" value={fmt(postsData?.summary?.comments)} />
                <SmallStat label="Guardados" value={fmt(postsData?.summary?.saved)} />
                <SmallStat label="Impresiones" value={fmt(postsData?.summary?.views)} />
                <SmallStat label="Interacciones" value={fmt(postsData?.summary?.interactions)} />
              </div>
              <PostsTable posts={postsData?.posts || []} />
              <HashtagsBlock hashtags={postsData?.hashtags || []} />
            </>
          )}
        </TabsContent>

        {/* REELS */}
        <TabsContent value="reels" className="mt-4 space-y-4" data-testid="ig-reels-tab">
          {loadingReels ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-md skeleton" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="ig-reels-summary">
                <SmallStat label="Reels" value={fmt(reelsData?.summary?.count)} />
                <SmallStat label="Alcance prom." value={fmtDec(reelsData?.summary?.avg_reach, 0)} />
                <SmallStat label="Engagement" value={fmtPct(reelsData?.summary?.avg_engagement)} />
                <SmallStat label="Visualizaciones" value={fmt(reelsData?.summary?.views)} />
                <SmallStat label="Likes" value={fmt(reelsData?.summary?.likes)} />
                <SmallStat label="Comentarios" value={fmt(reelsData?.summary?.comments)} />
                <SmallStat label="Compartidos" value={fmt(reelsData?.summary?.shares)} />
                <SmallStat label="Guardados" value={fmt(reelsData?.summary?.saved)} />
              </div>
              <ReelsTable reels={reelsData?.reels || []} />
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ─────────────── Subcomponents ─────────────── */

function KpiCard({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    pink: { bg: "bg-pink-50", border: "border-pink-100", text: "text-pink-600" },
    purple: { bg: "bg-purple-50", border: "border-purple-100", text: "text-purple-600" },
    sky: { bg: "bg-sky-50", border: "border-sky-100", text: "text-sky-600" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-600" },
    rose: { bg: "bg-rose-50", border: "border-rose-100", text: "text-rose-600" },
    amber: { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-600" },
    indigo: { bg: "bg-indigo-50", border: "border-indigo-100", text: "text-indigo-600" },
    slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className={`w-7 h-7 rounded-md ${t.bg} border ${t.border} flex items-center justify-center mb-1.5`}>
        <Icon className={`h-3.5 w-3.5 ${t.text}`} />
      </div>
      <div className="text-lg font-display font-semibold text-slate-900 leading-tight tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-2.5">
      <div className="text-sm font-display font-semibold text-slate-900 tabular-nums leading-tight">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 truncate">{label}</div>
    </div>
  );
}

function PostsTable({ posts }) {
  const [sortBy, setSortBy] = useState("reach");
  const sorted = useMemo(
    () => [...posts].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0)),
    [posts, sortBy]
  );

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Publicaciones</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{posts.length} posts en el período</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5">
          {[{ v: "reach", l: "Alcance" }, { v: "likes", l: "Likes" }, { v: "engagement", l: "Engagement" }, { v: "comments", l: "Coment." }].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setSortBy(opt.v)}
              data-testid={`ig-posts-sort-${opt.v}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                sortBy === opt.v
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>
      {sorted.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500" data-testid="ig-posts-empty">Sin publicaciones en el período</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ig-posts-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5 font-medium">Post</th>
                <th className="px-3 py-2.5 font-medium">Tipo</th>
                <th className="px-3 py-2.5 font-medium">Fecha</th>
                <th className="px-3 py-2.5 font-medium text-right">Alcance</th>
                <th className="px-3 py-2.5 font-medium text-right">Vistas</th>
                <th className="px-3 py-2.5 font-medium text-right">Likes</th>
                <th className="px-3 py-2.5 font-medium text-right">Coment.</th>
                <th className="px-3 py-2.5 font-medium text-right">Guardados</th>
                <th className="px-3 py-2.5 font-medium text-right">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((p) => (
                <tr key={p.post_id} className="hover:bg-slate-50" data-testid={`ig-post-row-${p.post_id}`}>
                  <td className="px-4 py-2.5">
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded-sm flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-200 rounded-sm flex-shrink-0" />
                      )}
                      <span className="text-slate-900 group-hover:text-pink-600 line-clamp-2 max-w-[260px]">
                        {(p.content || "(sin texto)").substring(0, 90)}
                      </span>
                    </a>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                      {prettyIgType(p.type)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(p.published_at)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{fmt(p.reach)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(p.views)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(p.likes)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(p.comments)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(p.saved)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtPct(p.engagement)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReelsTable({ reels }) {
  const [sortBy, setSortBy] = useState("views");
  const sorted = useMemo(
    () => [...reels].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0)),
    [reels, sortBy]
  );

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Reels</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{reels.length} reels en el período</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5">
          {[{ v: "views", l: "Vistas" }, { v: "reach", l: "Alcance" }, { v: "likes", l: "Likes" }, { v: "engagement", l: "Engagement" }].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setSortBy(opt.v)}
              data-testid={`ig-reels-sort-${opt.v}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                sortBy === opt.v
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>
      {sorted.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500" data-testid="ig-reels-empty">Sin reels en el período</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ig-reels-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5 font-medium">Reel</th>
                <th className="px-3 py-2.5 font-medium">Fecha</th>
                <th className="px-3 py-2.5 font-medium text-right">Vistas</th>
                <th className="px-3 py-2.5 font-medium text-right">Alcance</th>
                <th className="px-3 py-2.5 font-medium text-right">Likes</th>
                <th className="px-3 py-2.5 font-medium text-right">Coment.</th>
                <th className="px-3 py-2.5 font-medium text-right">Comp.</th>
                <th className="px-3 py-2.5 font-medium text-right">Guardados</th>
                <th className="px-3 py-2.5 font-medium text-right">Dur.</th>
                <th className="px-3 py-2.5 font-medium text-right">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((r) => (
                <tr key={r.reel_id} className="hover:bg-slate-50" data-testid={`ig-reel-row-${r.reel_id}`}>
                  <td className="px-4 py-2.5">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group">
                      {r.image_url ? (
                        <img src={r.image_url} alt="" className="w-10 h-10 object-cover rounded-sm flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-200 rounded-sm flex-shrink-0" />
                      )}
                      <span className="text-slate-900 group-hover:text-pink-600 line-clamp-2 max-w-[260px]">
                        {(r.content || "(sin texto)").substring(0, 90)}
                      </span>
                    </a>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(r.published_at)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{fmt(r.views)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(r.reach)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(r.likes)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(r.comments)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(r.shares)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(r.saved)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtDec(r.duration_seconds, 0)}s</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtPct(r.engagement)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HashtagsBlock({ hashtags }) {
  if (!hashtags || hashtags.length === 0) return null;
  const top = hashtags.slice(0, 12);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="ig-hashtags">
      <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
        <Hash className="h-3.5 w-3.5" /> Top hashtags
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {top.map((h) => (
          <div key={h.hashtag} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-pink-600 truncate">#{h.hashtag}</div>
              <div className="text-[10px] text-slate-500">{fmt(h.count)} posts</div>
            </div>
            <div className="text-right">
              <div className="text-xs font-semibold text-slate-900 tabular-nums">{fmt(h.views)}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">vistas</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IGDemographicsBlock({ data }) {
  if (!data?.configured || !data.channels?.length) return null;
  const merged = mergeIgDist(data.channels.map((c) => c.demographics));
  const hasAny = Object.values(merged).some((arr) => arr.length > 0);
  if (!hasAny) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-6 text-center text-xs text-slate-500" data-testid="ig-demographics-empty">
        Aún no hay datos demográficos disponibles para esta cuenta.
      </div>
    );
  }
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="ig-demographics">
      <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
        <Globe2 className="h-3.5 w-3.5" /> Demografía de la audiencia
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DistList title="Edad" rows={merged.age} />
        <DistList title="Género" rows={merged.gender} />
        <DistList title="País (Top 10)" rows={merged.country.slice(0, 10)} />
        <DistList title="Ciudad (Top 10)" rows={merged.city.slice(0, 10)} />
      </div>
    </section>
  );
}

function mergeIgDist(list) {
  const out = { age: [], gender: [], country: [], city: [] };
  for (const dem of list) {
    if (!dem) continue;
    for (const k of Object.keys(out)) {
      const items = dem[k] || [];
      out[k].push(...items);
    }
  }
  for (const k of Object.keys(out)) {
    const agg = {};
    for (const item of out[k]) {
      const lbl = item.key || item.label || item.name || "(?)";
      const val = Number(item.value || item.percent || 0);
      agg[lbl] = (agg[lbl] || 0) + val;
    }
    out[k] = Object.entries(agg)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }
  return out;
}

function DistList({ title, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        <h4 className="text-[11px] font-semibold text-slate-700 mb-2">{title}</h4>
        <p className="text-xs text-slate-400">Sin datos</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div>
      <h4 className="text-[11px] font-semibold text-slate-700 mb-2">{title}</h4>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.label} className="text-xs">
            <div className="flex justify-between mb-0.5">
              <span className="text-slate-700 truncate pr-2">{r.label}</span>
              <span className="text-slate-900 font-medium tabular-nums">{r.value.toFixed(1)}%</span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full" style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function prettyIgType(t) {
  if (!t) return "Post";
  const m = {
    "FEED_IMAGE": "Imagen",
    "FEED_CAROUSEL_ALBUM": "Carrusel",
    "FEED_VIDEO": "Video",
    "REEL": "Reel",
    "STORY": "Story",
  };
  return m[t] || t.replace(/_/g, " ").toLowerCase();
}
